import { Avatar, Box, Button, Flex, Grid, GridItem, HStack, Icon, Spinner, Text, VStack } from "@chakra-ui/react";
import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { FiArrowLeft, FiMessageCircle, FiUsers } from "react-icons/fi";
import { DiscoverFeed, getDiscoverFeed, getPortalPost, WallPost } from "@lubbezposrednio-frontend/core";
import { OrganizerPage } from "../OrganizerDashboardView/components/OrganizerShell";
import WallPostCard from "../GroupWallView/WallPostCard";
import { mediaUrl } from "../GroupWallView/wallShared";
import { VerifiedBadge } from "../../components/VerifiedBadge";

const SidebarCard: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
  <Box bg="white" borderRadius="16px" border="1px solid rgba(67,0,152,0.08)" boxShadow="0 6px 20px rgba(67,0,152,0.05)" p="16px 18px">
    <Text fontSize="13px" fontWeight="800" textTransform="uppercase" letterSpacing="0.04em" color="gray.400" mb="12px">{title}</Text>
    {children}
  </Box>
);

// Permalink pojedynczego posta portalowego (deep-link z powiadomień) + kontekstowy sidebar Społeczności.
const PortalPostDetailView: React.FC = () => {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [post, setPost] = useState<WallPost | null>(null);
  const [suggest, setSuggest] = useState<DiscoverFeed | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    document.title = "Post";
    setLoading(true);
    getPortalPost(id)
      .then(r => setPost(r.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false));
    getDiscoverFeed().then(r => setSuggest(r.data)).catch(() => undefined);
  }, [id]);

  const sidebar = (
    <VStack align="stretch" spacing="14px" position={{ lg: "sticky" }} top={{ lg: "16px" }}>
      <SidebarCard title="Społeczność">
        <Text fontSize="13.5px" color="gray.500" mb="11px">Publiczny post w Społeczności. Wróć do feedu, aby zobaczyć więcej.</Text>
        <Button as={Link} to="/organizator/odkrywaj" w="100%" bg="primary" color="white" borderRadius="11px" fontWeight="700" size="sm" _hover={{ bg: "#360079" }}>
          Otwórz Społeczność
        </Button>
      </SidebarCard>

      {suggest && suggest.suggested_profiles.length > 0 && (
        <SidebarCard title="Polecane konta">
          <VStack align="stretch" spacing="2px">
            {suggest.suggested_profiles.slice(0, 5).map(p => (
              <HStack key={p.id} as={Link} to={`/profil/${p.profile_ref}`} spacing="10px" p="7px" borderRadius="11px" _hover={{ bg: "#FAF8FE" }}>
                <Avatar size="sm" name={p.name} src={mediaUrl(p.avatar_url)} bg="primary" color="white" />
                <Box minW={0} flex="1">
                  <HStack spacing="4px" minW={0}>
                    <Text fontSize="14px" fontWeight="700" color="primary" noOfLines={1}>{p.name}</Text>
                    {p.verified && <VerifiedBadge size="13px" />}
                  </HStack>
                  <Text fontSize="11.5px" color="gray.400" noOfLines={1}>{p.profile_ref}</Text>
                </Box>
              </HStack>
            ))}
          </VStack>
        </SidebarCard>
      )}

      {suggest && suggest.suggested_groups.length > 0 && (
        <SidebarCard title="Polecane grupy">
          <VStack align="stretch" spacing="2px">
            {suggest.suggested_groups.slice(0, 5).map(g => (
              <HStack key={g.id} as={Link} to={`/organizator/grupy/${g.id}`} spacing="10px" p="7px" borderRadius="11px" _hover={{ bg: "#FAF8FE" }}>
                <Avatar size="sm" boxSize="36px" borderRadius="11px" name={g.name} src={mediaUrl(g.avatar_url)} bg="rgba(67,0,152,0.1)" color="primary" icon={<Icon as={FiUsers} boxSize="16px" />} />
                <Box minW={0} flex="1">
                  <Text fontSize="14px" fontWeight="700" color="primary" noOfLines={1}>{g.name}</Text>
                  <Text fontSize="11.5px" color="gray.400">{g.members_count} {g.members_count === 1 ? "członek" : "członków"}</Text>
                </Box>
              </HStack>
            ))}
          </VStack>
        </SidebarCard>
      )}
    </VStack>
  );

  return (
    <OrganizerPage>
      <HStack as={Link} to="/organizator/odkrywaj" spacing="8px" color="gray.500" fontSize="14px" fontWeight="600" mb="16px" _hover={{ color: "primary" }} w="fit-content">
        <FiArrowLeft />
        <Text>Społeczność</Text>
      </HStack>

      {loading ? (
        <Flex justify="center" py="80px"><Spinner color="primary" size="lg" thickness="3px" /></Flex>
      ) : error || !post ? (
        <Flex direction="column" align="center" justify="center" py="70px" bg="white" borderRadius="20px" border="1px dashed rgba(67,0,152,0.2)">
          <Icon as={FiMessageCircle} boxSize="26px" color="primary" mb="12px" />
          <Text color="primary" fontWeight="800" mb="4px">Nie znaleziono posta</Text>
          <Text color="gray.500" fontSize="14px" textAlign="center" maxW="360px">Post mógł zostać usunięty.</Text>
          <Button as={Link} to="/organizator/odkrywaj" mt="16px" bg="primary" color="white" borderRadius="11px" fontWeight="700" _hover={{ bg: "#360079" }}>
            Wróć do Społeczności
          </Button>
        </Flex>
      ) : (
        <Grid templateColumns={{ base: "1fr", lg: "1fr 320px" }} gap="16px" alignItems="start">
          <GridItem minW={0}>
            <WallPostCard
              post={post}
              groupId=""
              isModerator={false}
              onChange={updater => setPost(p => (p ? updater(p) : p))}
              onRemove={() => navigate("/organizator/odkrywaj")}
            />
          </GridItem>
          <GridItem display={{ base: "none", lg: "block" }}>{sidebar}</GridItem>
        </Grid>
      )}
    </OrganizerPage>
  );
};

export default PortalPostDetailView;
